import React, { useEffect, useState } from 'react';
import { insiderApi } from '../services/api';

interface ScoreItem {
  portfolio: string;
  exchange: string;
  ticker: string;
  name: string;
  score: number;
}

interface ScoreDetail {
  type: string;
  price: number;
  shares: number;
  date: string;
  title: string | null;
  sharesMultiplier: number;
  timeMultiplier: number;
  priceMultiplier: number;
  titleMultiplier: number;
  amountMultiplier: number;
  score: number;
}

type TabKey = 'portfolio' | 'watchlist' | 'search';

const InsiderActivityPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('portfolio');
  const [items, setItems] = useState<ScoreItem[]>([]);
  const [details, setDetails] = useState<ScoreDetail[]>([]);
  const [searchSummary, setSearchSummary] = useState<ScoreItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTicker, setSearchTicker] = useState('');

  const loadTab = (t: TabKey) => {
    if (t === 'search') { setLoading(false); return; }
    setLoading(true);
    const fn = t === 'portfolio' ? insiderApi.portfolio : insiderApi.watchlist;
    fn().then((res) => setItems(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadTab(tab); }, [tab]);

  const handleSearch = () => {
    if (!searchTicker) return;
    setLoading(true);
    insiderApi.search(searchTicker)
      .then((res) => {
        setSearchSummary(res.data.summary);
        setDetails(res.data.details);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const scoreColor = (s: number) => s >= 0 ? 'green' : 'red';

  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>Insider Activity Scoring</h4>
      <div style={styles.tabs}>
        {([
          { key: 'portfolio' as TabKey, label: 'Portfolio Ranking' },
          { key: 'watchlist' as TabKey, label: 'Watchlist Ranking' },
          { key: 'search' as TabKey, label: 'Ticker Search' },
        ]).map((t) => (
          <span
            key={t.key}
            onClick={() => { setTab(t.key); setDetails([]); setSearchSummary(null); }}
            style={{ ...styles.tab, ...(tab === t.key ? styles.activeTab : {}) }}
          >
            {t.label}
          </span>
        ))}
      </div>

      {tab === 'search' && (
        <div style={styles.searchBar}>
          <label>Ticker: </label>
          <input
            value={searchTicker}
            onChange={(e) => setSearchTicker(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={styles.input}
          />
          <input type="button" value="Search" onClick={handleSearch} style={{ ...styles.button, marginLeft: '1em' }} />
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>
      ) : tab !== 'search' ? (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{tab === 'portfolio' ? 'Portfolio' : 'List'}</th>
              <th style={styles.th}>Exchange</th>
              <th style={styles.th}>Ticker</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Score</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const rowBg = idx % 2 === 0 ? '#fff' : '#e6f2ff';
              return (
                <tr key={`${item.exchange}:${item.ticker}`} style={{ backgroundColor: rowBg }}>
                  <td style={styles.td}>{item.portfolio}</td>
                  <td style={styles.td}>{item.exchange}</td>
                  <td style={styles.td}><strong>{item.ticker}</strong></td>
                  <td style={styles.td}>{item.name}</td>
                  <td style={{ ...styles.td, textAlign: 'right', color: scoreColor(item.score), fontWeight: 'bold' }}>
                    {item.score.toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <>
          {searchSummary && (
            <table style={{ ...styles.table, width: '60%', margin: '0 auto 1em' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Portfolio</th>
                  <th style={styles.th}>Exchange</th>
                  <th style={styles.th}>Ticker</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Score</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={styles.td}>{searchSummary.portfolio}</td>
                  <td style={styles.td}>{searchSummary.exchange}</td>
                  <td style={styles.td}><strong>{searchSummary.ticker}</strong></td>
                  <td style={styles.td}>{searchSummary.name}</td>
                  <td style={{ ...styles.td, textAlign: 'right', color: scoreColor(searchSummary.score), fontWeight: 'bold' }}>
                    {searchSummary.score.toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}

          {details.length > 0 && (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Price</th>
                  <th style={styles.th}>Shares</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Shares Mult.</th>
                  <th style={styles.th}>Time Mult.</th>
                  <th style={styles.th}>Price Mult.</th>
                  <th style={styles.th}>Title Mult.</th>
                  <th style={styles.th}>Amount Mult.</th>
                  <th style={styles.th}>Score</th>
                </tr>
              </thead>
              <tbody>
                {details.map((d, idx) => {
                  const rowBg = idx % 2 === 0 ? '#fff' : '#e6f2ff';
                  const isBuy = d.type === 'P' || d.type === 'Buy';
                  return (
                    <tr key={idx} style={{ backgroundColor: rowBg }}>
                      <td style={{ ...styles.td, textAlign: 'center', color: isBuy ? 'green' : 'red', fontWeight: 'bold' }}>
                        {d.type === 'P' ? 'Buy' : d.type === 'S' ? 'Sell' : d.type}
                      </td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{d.price.toFixed(2)}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{d.shares.toLocaleString()}</td>
                      <td style={styles.td}>{d.date}</td>
                      <td style={styles.td}>{d.title || '-'}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{d.sharesMultiplier.toFixed(4)}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{d.timeMultiplier.toFixed(4)}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{d.priceMultiplier.toFixed(4)}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{d.titleMultiplier.toFixed(4)}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{d.amountMultiplier.toFixed(4)}</td>
                      <td style={{ ...styles.td, textAlign: 'right', fontWeight: 'bold' }}>{d.score.toFixed(4)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '1em 0.5em', backgroundColor: '#fff', minHeight: 'calc(100vh - 6em)' },
  heading: { color: '#0069cc', marginTop: '1em', textAlign: 'center' },
  tabs: { display: 'flex', gap: '0', marginBottom: '1em', borderBottom: '2px solid #0d58e6' },
  tab: { padding: '6px 16px', cursor: 'pointer', fontSize: '0.85em', fontWeight: 'bold', backgroundColor: '#e6f2ff', border: '1px solid #0d58e6', borderBottom: 'none', marginRight: '2px' },
  activeTab: { backgroundColor: '#cce6ff', color: '#000' },
  searchBar: { backgroundColor: '#ececec', padding: '8px 12px', fontSize: '0.85em', marginBottom: '1em' },
  input: { padding: '2px 4px', fontFamily: 'Arial, sans-serif', fontSize: '1em', width: '10em' },
  button: { padding: '4px 16px', backgroundColor: '#ccdcfc', color: '#000', border: '1px solid #808080', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  table: { width: '100%', marginTop: '0.5em', border: '2px solid #0d58e6', borderCollapse: 'collapse' as const },
  th: { border: '1px solid #0d58e6', padding: '4px 6px', backgroundColor: '#cce6ff', fontWeight: 'bold', fontSize: '0.8em', textAlign: 'center' as const },
  td: { border: '1px solid #0d58e6', padding: '3px 6px', fontSize: '0.8em' },
};

export default InsiderActivityPage;
